/**
 * Auto-create photobook design on app load
 * Creates a 16-page photobook with:
 * - Page 1: Cover page (5374x3578px)
 * - Pages 2-16: Content pages with vertical shadow line in the middle
 */

const PAGE_WIDTH = 5374;
const PAGE_HEIGHT = 3578;
const TOTAL_PAGES = 16;

// Flag to track if auto-create has been attempted
let autoCreateAttempted = false;

/**
 * Create vertical shadow line (spine/hinge guide) in the middle of a page
 */
async function createSpineLine(session: any, pageIndex: number): Promise<void> {
  console.log(`[auto-create] Creating spine line for page ${pageIndex}`);
  
  const centerX = PAGE_WIDTH / 2;
  
  try {
    // Create a vertical line shape element with dashed stroke
    const shapeConfig = {
      top: 0,
      left: Math.round(centerX),
      width: 1,
      height: PAGE_HEIGHT,
      rotation: 0,
      transparency: 0,
      locked: true, // Lock the spine line so users don't accidentally move it
      viewBox: {
        top: 0,
        left: 0,
        width: 64,
        height: 64
      },
      paths: [
        {
          d: "M 32 0 L 32 64", // Vertical line in the middle of viewBox
          stroke: {
            colorContainer: {
              type: 'solid',
              color: '#999999' // Gray color for shadow line
            },
            weight: 2,
            dashPattern: [5, 5] // Dashed pattern: 5px dash, 5px gap
          }
          // No fill - this is a stroke-only element
        }
      ]
    };
    
    const shapeElementState = session.helpers.elementStateBuilder.createShapeElement(shapeConfig);
    const insertedElement = session.page.elements.insertAfter(undefined, shapeElementState);
    
    console.log(`[auto-create] ✅ Created spine line for page ${pageIndex}`);
  } catch (error) {
    console.error(`[auto-create] Failed to create spine line for page ${pageIndex}:`, error);
  }
}

/**
 * Create pages within an existing design session
 * This should be called from within an openDesign callback
 */
export async function createPhotobookPages(session: any): Promise<void> {
  console.log(`[auto-create] Creating photobook pages within existing session`);

  try {
    // Check if we have access to design.addPage
    if (!session.design?.addPage) {
      console.warn(`[auto-create] design.addPage not available - cannot create pages`);
      console.log(`[auto-create] Current page will be used as the first page`);

      // At least try to resize the current page
      if (session.page.resize) {
        await session.page.resize({
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT
        });
        console.log(`[auto-create] ✅ Resized current page to ${PAGE_WIDTH}x${PAGE_HEIGHT}px`);
      }

      return;
    }

    // Get current page count
    const currentPageCount = session.design.pages?.length || 1;
    console.log(`[auto-create] Current page count: ${currentPageCount}`);

    // Check if we already have 16 pages
    if (currentPageCount >= TOTAL_PAGES) {
      console.log(`[auto-create] Design already has ${currentPageCount} pages - skipping`);
      return;
    }

    // Calculate how many pages we need to add
    const pagesToAdd = TOTAL_PAGES - currentPageCount;
    console.log(`[auto-create] Adding ${pagesToAdd} pages to reach ${TOTAL_PAGES} total pages`);

    // Add pages
    for (let i = 0; i < pagesToAdd; i++) {
      const pageNumber = currentPageCount + i + 1;
      console.log(`[auto-create] Creating page ${pageNumber}/${TOTAL_PAGES}`);

      try {
        const newPage = await session.design.addPage({
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT
        });

        console.log(`[auto-create] ✅ Created page ${pageNumber}`);

        // Add spine line to content pages (pages 2-16)
        if (pageNumber > 1) {
          // Switch to the new page to add the spine line
          await session.setCurrentPage(newPage);
          await createSpineLine(session, pageNumber);
        }
      } catch (error) {
        console.error(`[auto-create] Failed to create page ${pageNumber}:`, error);
      }
    }

    // Switch back to the first page (cover page)
    if (session.design.pages && session.design.pages.length > 0) {
      await session.setCurrentPage(session.design.pages[0]);
      console.log(`[auto-create] ✅ Switched back to cover page`);
    }

    console.log(`[auto-create] ✅ Successfully created photobook with ${TOTAL_PAGES} pages`);
  } catch (error) {
    console.error(`[auto-create] Error creating photobook pages:`, error);
    throw error;
  }
}

/**
 * Initialize auto-create - this is now a no-op
 * The actual creation happens when the user first interacts with the app
 */
export function initAutoCreateDesign(): void {
  console.log(`[auto-create] Auto-create initialization - will be triggered on first design interaction`);
  // The actual page creation will be triggered from the main app when a design session is opened
}



